/** @module GamePlayActions */

import _ from "lodash";

import * as actionTypes from "../ActionTypes";
import { generateDeck } from "./CardsActions";
import { setEruptionCounter } from "./FlagsActions";
import { addGrid } from "./GridActions";
import { updateInstructions, setStageInStore } from "./MessageActions";
import {
  setPlayerArray,
  addPlayers,
  setPlayerTurn,
  addActivePlayer
} from "./PlayersActions";
import { generatePile } from "./TilesActions";
import { playerColors } from "../../data/playerData";
import { gridSquares } from "../../data/gridData";
import * as constant from "../../data/constants";

/**
 * @function addRecommendationsToStore
 * @description adds initial grid to GamePlayState store
 * @param {Array} recommendations - array or recommendations
 */
export const addRecommendationsToStore = recommendations => ({
  type: actionTypes.ADD_RECOMMENDATIONS,
  payload: recommendations
});

/**
 * @function addRecommendations
 * @description dispatches initial grid to addRecommendationsToStore
 * @param {Array} recommendations - array or recommendations
 */
export const addRecommendations = recommendations => dispatch => {
  dispatch(addRecommendationsToStore(recommendations));
};

/**
 * @function setPlacedRelativesToStore
 * @description sets relatives array
 * @param {Array} relatives - array or relatives
 */
export const setPlacedRelativesToStore = relatives => ({
  type: actionTypes.SET_PLACED_RELATIVES,
  payload: relatives
});

/**
 * @function setPlacedRelatives
 * @description dispatches initial grid to setPlacedRelativesToStore
 * @param {Array} relatives - array or relatives
 */
export const setPlacedRelatives = relatives => dispatch => {
  dispatch(setPlacedRelativesToStore(relatives));
};

/**
 * @function saveGameSettings
 * @description saves dev-level game settings
 * @param {Object} settings
 */
export const saveGameSettings = settings => ({
  type: actionTypes.SAVE_GAME_SETTINGS,
  payload: settings
});

/**
 * @function selectPerson
 * @description saves selected person
 * @param {Object} personObj
 */
export const selectPerson = personObj => ({
  type: actionTypes.SELECT_PERSON,
  payload: personObj
});

/**
 * @function gameSetup
 * @description takes start game values and dispatches initial game store values
 * @param {Object} details playerState details object
 * @param {Number} startPlayer which player starts
 * @param {Object} gameSettings gameSettings object
 */
export const gameSetup = (
  details,
  startPlayer,
  gameSettings = {}
) => async dispatch => {
  const theseDetails = { ...details };

  const playersArray = Object.keys(theseDetails);

  if (gameSettings.prePopulate) {
    // START PRE-POPULATION (TEST)
    const gridKeys = Object.keys(gridSquares);
    gridKeys.forEach(grid => {
      const potentialPop = _.get(gridSquares, `${grid}.buildingCapacity`, 0);
      const actualPop =
        gameSettings.startPhase === 2
          ? Math.ceil(Math.random() * potentialPop)
          : Math.floor(Math.random() * potentialPop);
      const occupants = [];
      for (let i = 0; i < actualPop; i += 1) {
        const rand = Math.floor(Math.random() * playersArray.length);
        const player = playersArray[rand];
        const id = `P${rand}-${theseDetails[player].population.length}s`;
        const personObj = {
          id,
          player,
          gender: Math.floor(Math.random() * 2) === 1 ? "male" : "female"
        };
        occupants.push(personObj);
        theseDetails[player].population.push(personObj);
        theseDetails[player].totalPieces =
          _.get(theseDetails, `${player}.totalPieces`, 0) + 1;
      }
      gridSquares[grid].occupants = occupants;
    });
    // END PRE-POPULATION (TEST)
  }

  if (gameSettings.startPhase > 0) {
    await dispatch(setStageInStore(gameSettings.startPhase));
  }
  await dispatch(saveGameSettings(gameSettings));
  await dispatch(addGrid(gridSquares));
  await dispatch(setEruptionCounter(gameSettings.noEruption ? 0 : 6));
  await dispatch(setPlayerArray(playersArray));
  await dispatch(addPlayers(theseDetails));
  await dispatch(setPlayerTurn(parseFloat(startPlayer) - 1));
  await dispatch(addActivePlayer(playersArray[startPlayer]));
  await dispatch(generateDeck(!!(gameSettings.startPhase === 1)));
  await dispatch(generatePile(gameSettings.wildLava));
  await dispatch(
    updateInstructions({
      text: `${details[`player${startPlayer}`].name}: ${constant.PLAY}`,
      color: playerColors[parseFloat(startPlayer) - 1]
    })
  );
};
